/**
 * TeamListRow — one team in a list of teams, at either density.
 *
 * 🔴 WHY THIS FILE EXISTS. Ben on the deployed room report: the team list and
 * the seat switcher were nondescript pills — a name in a rounded box, nothing
 * else, in a product whose whole subject is what each of those teams did with
 * its money. Two places drew a list of teams and both of them threw away every
 * fact the page already held about them.
 *
 * 🔴 ONE COMPONENT, TWO DENSITIES, NEVER TWO IMPLEMENTATIONS. The room strip
 * wants a full-width row; the switcher wants something the width of a chip that
 * a reader can tap between seats. Those are the SAME row with different padding
 * and one column dropped, and the moment they are two files they start
 * disagreeing about what a grade colour is — which is exactly the divergence
 * RoomReport.jsx and SpendBreakdown.jsx were each written to end. `density` is
 * a prop. The only structural difference is the tag: a row with an `onClick` is
 * a button and carries the tab semantics its caller asks for, a row without one
 * is a div.
 *
 * WHAT A ROW MAY CLAIM, and it is the caller who decides:
 *   badge          a LETTER when the caller has a real room grade, otherwise
 *                  the ordinal points rank, otherwise nothing. Grade colour
 *                  only when `badgeIsGrade` — the same flag and the same
 *                  GRADE_COLOR the room cards use, so a letter is never one
 *                  colour here and another there.
 *   surplus        absent, not zero, on a roster typed without dollars: the
 *                  row says "unpriced" rather than showing +$0, which is the
 *                  page-wide absent-not-defaulted rule.
 *   the spend bar  the Chunk H bar at its mini density, drawn only from a
 *                  breakdown the caller could build. No breakdown, no bar.
 *   the build      the shape the team's money made, as a monochrome chip beside
 *                  the name. Absent on an unpriced roster, never guessed, and
 *                  never coloured — see BuildChip.jsx for why the palette is
 *                  part of the claim.
 *
 * 🔴 IT CLAIMS NOTHING THE CALLER DID NOT HAND IT. There is deliberately no
 * grading, ranking or benchmarking in this file. The room strip is not allowed
 * to show letters until the room is full (TeamRoomBar's header carries that
 * argument) and a component that could invent one would make that rule a matter
 * of remembering to pass the right prop.
 *
 * MOTION is the dials file's, at the density's own helper: a full row lifts
 * like a card, a chip arrives like a chip. Reduced motion renders both finished
 * — no duration is written here, which rateMotion.test.js greps for.
 */

import { theme } from "../../data/theme.js";
import { C, SZ } from "../Submit/submitStyles.js";
import BuildChip from "./BuildChip.jsx";
import { GRADE_COLOR, NONE, money, signedMoney } from "./GroupCard.jsx";
import { cardEnter, chipEnter, prefersReducedMotion } from "./rateMotion.js";
import { SpendBar } from "./SpendBreakdown.jsx";

/**
 * The two densities, as data rather than as branches sprinkled through the JSX.
 *
 * `full` is the room strip's row: the whole width, the points column, a rank
 * number in front. `compact` is the switcher's card: no points column, because
 * a strip of ten chips each carrying four numbers is a wall rather than a
 * control, and the row below it is where a reader reads.
 */
export const ROW_DENSITY = {
  full: { badge: 34, badgeFont: 15, pad: "8px 10px", gap: 10, showPoints: true, showRank: true },
  compact: { badge: 24, badgeFont: 12, pad: "7px 10px", gap: 8, showPoints: false, showRank: false },
};

/** Green up, red down, faint when there is nothing to be either. */
function surplusColorOf(surplus) {
  if (surplus == null) return C.text.faint;
  if (surplus > 0.5) return C.status.success;
  if (surplus < -0.5) return C.status.danger;
  return C.text.secondary;
}

export default function TeamListRow({
  density = "full",
  index = 0,
  rank,
  name,
  badge,
  badgeIsGrade,
  week1,
  surplus,
  spent,
  breakdown,
  build,
  hasReport = true,
  active,
  onClick,
  testId,
  role,
  ariaSelected,
}) {
  const D = ROW_DENSITY[density] || ROW_DENSITY.full;
  // Read once per render, exactly as the room cards read it. The entrance is a
  // CSS animation string that depends only on `index` and this flag, so a
  // re-render — picking a different seat, say — cannot replay it.
  const still = prefersReducedMotion();
  const Tag = onClick ? "button" : "div";
  const enter = density === "compact" ? chipEnter(index, still) : cardEnter(index, still);

  return (
    <Tag
      type={onClick ? "button" : undefined}
      onClick={onClick}
      role={role}
      aria-selected={ariaSelected}
      data-testid={testId}
      style={{
        display: "flex",
        alignItems: "center",
        gap: D.gap,
        width: density === "full" ? "100%" : undefined,
        boxSizing: "border-box",
        textAlign: "left",
        padding: D.pad,
        borderRadius: theme.radius.lg,
        // The active seat wears the brand border the pills wore, so the one
        // thing the old control did communicate is not lost in the rebuild.
        border: `1px solid ${active ? C.border.brand : C.border.default}`,
        background: active ? C.bg.raised : "transparent",
        color: C.text.primary,
        cursor: onClick ? "pointer" : "default",
        font: "inherit",
        ...enter,
      }}
    >
      {/* The row's place in the list it is part of, which is not the badge: the
          badge is a CLAIM about the team, this is just where it sits. */}
      {D.showRank && rank != null && (
        <span
          style={{
            width: 16,
            flexShrink: 0,
            fontSize: SZ.micro,
            fontWeight: 700,
            color: C.text.faint,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {rank}
        </span>
      )}
      {/* 🔴 THE BADGE CELL IS A COLUMN IN A LIST AND A DECORATION IN A CONTROL.
          A full-width list must keep its badge column aligned, so a row with no
          letter draws the dash — the page's "considered, and there is no value"
          mark. A chip is not a column: a strip of chips each wearing a dash says
          nothing and costs 24px, so the compact density drops it entirely. */}
      {(badge != null || density === "full") && (
      <span
        data-testid={testId ? `${testId}-badge` : undefined}
        style={{
          width: D.badge,
          height: D.badge,
          flexShrink: 0,
          borderRadius: theme.radius.md,
          background: C.bg.deep,
          border: `1px solid ${C.border.default}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: D.badgeFont,
          fontWeight: 900,
          // 🔴 GRADE COLOUR ONLY FOR A GRADE. A rank number in green would read
          // as a letter to anyone glancing, which is the whole point of the
          // room strip's refusal to grade a partial room.
          color: badgeIsGrade ? GRADE_COLOR(badge) : C.text.faint,
        }}
      >
        {badge == null ? NONE : badge}
      </span>
      )}
      <span style={{ flex: 1, minWidth: 0 }}>
        {/* The name can be long and the chip must not be squeezed out of a
            narrow row, so the name takes the give and the chip keeps its
            width. Same arrangement at both densities. */}
        <span style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
          <span
            style={{
              minWidth: 0,
              fontSize: density === "compact" ? SZ.small : SZ.body,
              fontWeight: active ? 800 : 700,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {name}
          </span>
          <BuildChip
            build={build}
            dense={density === "compact"}
            testId={testId ? `${testId}-build` : undefined}
          />
        </span>
        {/* 🔴 NO REPORT IS NOT AN UNPRICED REPORT. `hasReport: false` means the
            caller holds nothing about this seat yet — the roster step's switcher
            is drawn before any team is graded — and a row that answered that
            with "unpriced" would be reporting a measurement nobody took. A
            graded team with no dollars is the one that says unpriced. */}
        {hasReport && (
          <span
            style={{
              display: "block",
              fontSize: SZ.micro,
              color: C.text.secondary,
              fontVariantNumeric: "tabular-nums",
              whiteSpace: "nowrap",
            }}
          >
            {D.showPoints && (
              <span data-testid={testId ? `${testId}-pts` : undefined}>
                {week1 == null ? NONE : `${week1.toFixed(1)} pts`}
              </span>
            )}
            {D.showPoints && " · "}
            <span
              data-testid={testId ? `${testId}-surplus` : undefined}
              style={{ color: surplusColorOf(surplus), fontWeight: 700 }}
            >
              {surplus == null ? "unpriced" : `${signedMoney(surplus)} on ${money(spent)}`}
            </span>
          </span>
        )}
        {/* The Chunk H bar, at its mini density. Absent on a row whose caller
            had no breakdown to give it — never an empty strip. */}
        <SpendBar breakdown={breakdown} mini testId={testId ? `${testId}-bar` : undefined} />
      </span>
    </Tag>
  );
}
